import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Button } from '@quadminds/mindkit';
import { useToast } from '@quadminds/mindkit';
import { IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Tree } from 'react-arborist';
import { useTranslation } from 'react-i18next';
import { useGetKnowledgeFolders } from '../../../../../hooks/useGetFolder';
import { useMoveDocumentToFolder } from '../../../../../hooks/useMoveDocument';
import { useKnowledgeContext } from '../../../../../providers/Knowledge/Knowledge.provider';

interface MoveDialogProps {
    isOpen: boolean;
    currentFolderId: string;
    parentId?: string | null;
}

type Item = {
    id: string;
    name: string;
    children?: Item[];
};

function addChildrenAndReturnUpdatedTree(items: Item[], idToFind: string, newChildren?: Item[]): Item[] {
    const update = (nodes: Item[]): Item[] => {
        return nodes.map((node) => {
            if (node.id === idToFind) {
                const existingIds = new Set(node.children?.map((c) => c.id) ?? []);
                const uniqueChildren = (newChildren ?? []).filter((child) => !existingIds.has(child.id));

                return {
                    ...node,
                    children: node.children
                        ? [...node.children, ...uniqueChildren]
                        : uniqueChildren.length > 0
                        ? [...uniqueChildren]
                        : undefined,
                };
            } else if (node.children) {
                return {
                    ...node,
                    children: update(node.children),
                };
            } else {
                return node;
            }
        });
    };

    return update(items);
}

export function MoveDialog({ isOpen, currentFolderId }: MoveDialogProps) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const userInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']);
    const [selectedPath, setSelectedPath] = useState<{ id: string; name: string }>({
        id: userInfo?.currentOrganization || '',
        name: t('modals.moveFile.root'),
    });

    const [folders, setFolders] = useState<{ control: string | null; items: Item[] | null }>({
        control: null,
        items: null,
    });

    const { selectedItemToMove, selectedMultipleItemsToMove, clearSelecteItemToMove, handleDialogState } =
        useKnowledgeContext();
    const { mutateAsync, isPending } = useMoveDocumentToFolder();
    const [currentParentId, setCurrentParentId] = useState<string | null>(null);
    const [loadingFolderId, setLoadingFolderId] = useState<string | null>(null);

    const { data, isLoading: isLoadingFolders } = useGetKnowledgeFolders(
        userInfo?.currentOrganization || '',
        currentParentId !== null ? currentParentId : undefined,
        isOpen, // Only enable the query when the dialog is open
    );

    const { toast } = useToast();

    // Reset selected path when dialog opens
    useEffect(() => {
        if (isOpen) {
            setSelectedPath({
                id: userInfo?.currentOrganization || '',
                name: t('modals.moveFile.root'),
            });
            setCurrentParentId(null);
            setLoadingFolderId(null);
            setFolders({ control: null, items: null });
        }
    }, [isOpen, userInfo?.currentOrganization, t]);

    useEffect(() => {
        if (folders.items === null && data !== undefined) {
            setFolders({ control: null, items: data });
            setLoadingFolderId(null); // Clear loading state when root data loads
        }
        if (currentParentId && folders.control !== currentParentId && folders.items !== null && data !== undefined) {
            console.log('newData: ', data);
            const mergedFolders = addChildrenAndReturnUpdatedTree(folders.items, currentParentId || '', data);
            setFolders({ control: currentParentId, items: mergedFolders });
            setLoadingFolderId(null); // Clear loading state when subfolder data loads
        }
    }, [currentParentId, data, folders.control, folders.items]);

    const handleConfirm = async () => {
        if (selectedItemToMove !== null) {
            // Prevent moving item to its current location
            if (selectedPath.id === currentFolderId) {
                toast({
                    title: t('modals.moveFile.error.title'),
                    description: t('modals.moveFile.error.sameLocation'),
                    variant: 'destructive',
                });
                return;
            }

            const payload: {
                documentId: string;
                organizationId: string;
                destinationFolderId?: string;
            } = {
                documentId: selectedItemToMove.id,
                organizationId: userInfo?.currentOrganization || '',
            };

            // Only set destinationFolderId if not moving to root
            if (selectedPath.id !== userInfo?.currentOrganization) {
                payload.destinationFolderId = selectedPath.id;
            }

            try {
                await mutateAsync(payload);
                clearSelecteItemToMove();
                handleDialogState('isMoveDialogOpen', false);
                await queryClient.invalidateQueries({ queryKey: ['documents'] });
                toast({
                    title: t('modals.moveFile.success.title'),
                    description: t('modals.moveFile.success.description'),
                    variant: 'default',
                });
            } catch (error) {
                console.error('Move operation failed:', error);
                toast({
                    title: t('modals.moveFile.error.title'),
                    description: t('modals.moveFile.error.description'),
                    variant: 'destructive',
                });
            }
        } else if (selectedMultipleItemsToMove.length > 0) {
            // Prevent moving items to their current location
            if (selectedPath.id === currentFolderId) {
                toast({
                    title: t('modals.moveFile.error.title'),
                    description: t('modals.moveFile.error.sameLocation'),
                    variant: 'destructive',
                });
                return;
            }

            const payload = selectedMultipleItemsToMove.map((item) => ({
                documentId: item.id,
                organizationId: userInfo?.currentOrganization || '',
                destinationFolderId: selectedPath.id !== userInfo?.currentOrganization ? selectedPath.id : undefined,
            }));

            try {
                await Promise.all(payload.map((p) => mutateAsync(p)));
                clearSelecteItemToMove();
                handleDialogState('isMoveDialogOpen', false);
                await queryClient.invalidateQueries({ queryKey: ['documents'] });
                toast({
                    title: t('modals.moveFile.success.title'),
                    description: t('modals.moveFile.success.description'),
                    variant: 'default',
                });
            } catch (error) {
                console.error('Move operation failed:', error);
                toast({
                    title: t('modals.moveFile.error.title'),
                    description: t('modals.moveFile.error.description'),
                    variant: 'destructive',
                });
            }
        }
    };

    const handleClose = () => {
        clearSelecteItemToMove();
        handleDialogState('isMoveDialogOpen', false);
    };

    const thereAreNotSelectedItemsToMove = selectedItemToMove === null && selectedMultipleItemsToMove.length === 0;

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[500px]" data-testid="move-dialog">
                <DialogHeader>
                    <DialogTitle data-testid="move-dialog-title">{t('modals.moveFile.title')}</DialogTitle>
                    {selectedItemToMove && (
                        <p className="text-sm text-muted-foreground">
                            {t('modals.moveFile.subtitle', { name: selectedItemToMove.name })}
                        </p>
                    )}
                    {selectedMultipleItemsToMove.length > 0 && (
                        <p className="text-sm text-muted-foreground">
                            {t('modals.moveFile.subtitle2', { count: selectedMultipleItemsToMove.length })}
                        </p>
                    )}

                    {/* Breadcrumb navigation */}
                    {selectedPath.id !== userInfo?.currentOrganization && (
                        <div className="mt-2 flex items-center">
                            <button
                                type="button"
                                className="text-xs text-primary hover:underline hover:bg-primary/5 px-2 py-1 rounded transition-colors flex items-center gap-1"
                                onClick={() => {
                                    setSelectedPath({
                                        id: userInfo?.currentOrganization || '',
                                        name: t('modals.moveFile.root'),
                                    });
                                    setCurrentParentId(null);
                                    setLoadingFolderId(null);
                                }}
                                disabled={isLoadingFolders}
                            >
                                <span>←</span> {t('modals.moveFile.backToRoot')}
                            </button>
                        </div>
                    )}
                </DialogHeader>

                <div className="py-4">
                    <div className="border rounded-md h-[300px] overflow-y-auto p-2" data-testid="folders-list">
                        {isLoadingFolders && currentParentId === null ? (
                            <div className="flex items-center justify-center h-full">
                                <div className="flex flex-col items-center gap-2">
                                    <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"></div>
                                    <p className="text-sm text-muted-foreground">{t('modals.moveFile.loading')}</p>
                                </div>
                            </div>
                        ) : folders.items === null || folders.items.length === 0 ? (
                            <div className="flex items-center justify-center h-full text-muted-foreground">
                                {t('modals.moveFile.notSubFiles')}
                            </div>
                        ) : (
                            <Tree
                                rowHeight={42}
                                rowClassName="px-2"
                                data={folders.items}
                                padding={8}
                                disableDrag={true}
                                disableDrop={true}
                                disableEdit={true}
                                disableMultiSelection={true}
                                onSelect={(node) => {
                                    if (node[0]?.data) {
                                        if (node[0]?.data.children !== undefined) {
                                            node[0]?.toggle();
                                        }

                                        setSelectedPath({
                                            id: node[0]?.data?.id,
                                            name: node[0]?.data?.name,
                                        });

                                        // Set loading state and fetch subfolder contents
                                        if (!node[0]?.isOpen) {
                                            setLoadingFolderId(node[0]?.data?.id);
                                            setCurrentParentId(node[0]?.data?.id);
                                        }
                                    }
                                }}
                            >
                                {({ node, style }) => {
                                    /* This node instance can do many things. See the API reference. */
                                    return (
                                        <button
                                            key={node.id}
                                            style={{ ...style, paddingLeft: Number(style?.paddingLeft ?? 0) + 8 }}
                                            type="button"
                                            className={`w-full text-left p-2 rounded hover:bg-muted transition-colors flex items-center gap-2 ${
                                                selectedPath.id === node.data.id
                                                    ? 'bg-primary/10 border border-primary'
                                                    : ''
                                            }`}
                                            data-testid={`folder-option-${node.data.id}`}
                                            disabled={loadingFolderId === node.data.id}
                                        >
                                            <span role="img" aria-label="folder">
                                                📁
                                            </span>{' '}
                                            <span className="flex-1">{node.data.name}</span>
                                            {loadingFolderId === node.data.id && (
                                                <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full"></div>
                                            )}
                                        </button>
                                    );
                                }}
                            </Tree>
                        )}
                    </div>

                    <div className="mt-2 text-sm">
                        {t('modals.moveFile.selectedFolder')}
                        <span className="font-medium ml-1">{selectedPath.name}</span>
                        {isLoadingFolders && currentParentId !== null && (
                            <span className="ml-2 text-xs text-muted-foreground animate-pulse">
                                {t('modals.moveFile.loadingSubfolders')}
                            </span>
                        )}
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleClose} disabled={isPending} data-testid="move-cancel-btn">
                        {t('modals.common.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={isPending || thereAreNotSelectedItemsToMove}
                        data-testid="move-confirm-btn"
                    >
                        {isPending ? t('modals.moveFile.moving') : t('modals.moveFile.move')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
